import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export interface GmailAccount {
  id: number;
  emailAddress: string;
  kind: 'personal' | 'shared';
  ownerUserId: number | null;
  syncStatus: 'idle' | 'initial_backfill' | 'incremental' | 'paused' | 'error';
  syncError: string | null;
  lastSyncedAt: string | null;
  nickname: string | null;
  nicknameTagId: number | null;
  nicknameColor: string | null;
  /** Outbound From display name ("Tech Generation <tech@…>"). null = bare address. */
  senderName: string | null;
  /** False for inboxes connected before the gmail.settings.basic scope —
   *  they can't create the Gmail filters that power domain blocking until
   *  they're reconnected. */
  canBlockDomains: boolean;
  createdAt: string;
}

/** Update an account's outbound sender name (owner-only server-side). */
export function useUpdateSenderName() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: number; senderName: string | null }) => {
      const r = await fetch(`/api/accounts/${input.id}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ senderName: input.senderName }),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error((j as { error?: string }).error || `HTTP ${r.status}`);
      }
      return r.json();
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['accounts'] }),
  });
}

export function useAccounts() {
  return useQuery<{ accounts: GmailAccount[] }>({
    queryKey: ['accounts'],
    queryFn: async () => {
      const res = await fetch('/api/accounts', { credentials: 'include' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.json();
    },
    staleTime: 5_000,
    // Auto-refresh while any account is mid-backfill so the badge flips
    // from "initial_backfill" → "incremental" without a manual reload.
    refetchInterval: (q) => {
      const accounts = q.state.data?.accounts ?? [];
      const active = accounts.some((a) => a.syncStatus === 'initial_backfill');
      return active ? 5_000 : false;
    },
  });
}

export function useDisconnectAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const res = await fetch(`/api/accounts/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['accounts'] }),
  });
}

export function useSyncAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const res = await fetch(`/api/accounts/${id}/sync`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['accounts'] }),
  });
}

export function useSetAccountNickname() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: number; nickname: string | null; color?: string }) => {
      const r = await fetch(`/api/accounts/${input.id}/nickname`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ nickname: input.nickname, color: input.color }),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.error || `HTTP ${r.status}`);
      }
      return r.json();
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['accounts'] });
      void qc.invalidateQueries({ queryKey: ['threads'] });
      void qc.invalidateQueries({ queryKey: ['thread'] });
      void qc.invalidateQueries({ queryKey: ['tags'] });
    },
  });
}

// --- Share management ------------------------------------------------------

export interface ShareGrantee {
  id: number;
  email: string;
  name: string;
  picture: string | null;
  grantedAt: string;
}

export interface AccountShares {
  accountId: number;
  canManage: boolean;
  owner: { id: number; email: string; name: string; picture: string | null } | null;
  grantees: ShareGrantee[];
}

export function useAccountShares(accountId: number | null) {
  return useQuery<AccountShares>({
    queryKey: ['account-shares', accountId],
    enabled: accountId != null,
    queryFn: async () => {
      const r = await fetch(`/api/accounts/${accountId}/shares`, { credentials: 'include' });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return r.json();
    },
  });
}

export function useGrantAccountShare(accountId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (userId: number) => {
      const r = await fetch(`/api/accounts/${accountId}/shares`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ userId }),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.error || `HTTP ${r.status}`);
      }
      return r.json();
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['account-shares', accountId] });
      void qc.invalidateQueries({ queryKey: ['accounts'] });
    },
  });
}

export function useRevokeAccountShare(accountId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (userId: number) => {
      const r = await fetch(`/api/accounts/${accountId}/shares/${userId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.error || `HTTP ${r.status}`);
      }
      return r.json();
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['account-shares', accountId] });
      void qc.invalidateQueries({ queryKey: ['accounts'] });
    },
  });
}

export function startConnectGmail(
  kind: 'personal' | 'shared' = 'personal',
  opts?: { nickname?: string; nicknameColor?: string },
): void {
  const params = new URLSearchParams();
  params.set('kind', kind);
  if (kind === 'shared' && opts?.nickname) {
    params.set('nickname', opts.nickname);
    if (opts.nicknameColor) params.set('nickname_color', opts.nicknameColor);
  }
  window.location.href = `/api/oauth/google/connect?${params}`;
}
